import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Header } from './components/Header'
import { MainButtonContext, MainButtonSlot, type MainButtonConfig } from './components/MainButton'
import { Starfield } from './components/Starfield'
import { TabBar, type TabId } from './components/TabBar'
import { InstallBanner } from './components/InstallBanner'
import { InstallOffer } from './components/InstallOffer'
import { GetApp } from './components/GetApp'
import { IntroModal, IntroStrip } from './components/IntroOffer'
import { NotifyAsk } from './components/NotifyAsk'
import { drawCards, loc, todayKey } from './data/cards'
import { spreadById, type SpreadId, repeatCost } from './data/spreads'
import { haptic, telegramUser } from './lib/telegram'
import { takeNotifyTab } from './lib/notify-tab'
import { authMode, takeReturnScreen } from './lib/auth'
import { track } from './lib/analytics'
import { isStandalone } from './lib/install'
import { api } from './lib/api'
import { RewardModal } from './components/RewardModal'
import { sameDraw, type AskLine, type DrawnCard } from './lib/storage'
import { adsAvailable, showInterstitial } from './lib/ads'
import { Compat } from './screens/Compat'
import { Home } from './screens/Home'
import { Onboarding, OB_LAST } from './screens/Onboarding'
import { Profile } from './screens/Profile'
import { Reading } from './screens/Reading'
import { PickCard } from './screens/PickCard'
import { TwoPaths } from './screens/TwoPaths'
import { Catalog } from './screens/Catalog'
import { Fortune } from './screens/Fortune'
import { formatById, type FormatId } from './data/formats'
import { Stars } from './screens/Stars'
import { Invites } from './screens/Invites'
import { Login } from './screens/Login'
import { FREE_LIMIT_PER_DAY, useSirr } from './store'

type Screen = TabId | 'onboarding' | 'reading' | 'pick' | 'paths' | 'stars' | 'invites' | 'fortune' | 'login'

interface ActiveReading {
  spread: SpreadId
  cards: DrawnCard[]
  /** Месяц начала Колеса года; фиксируется в момент расклада. */
  startMonth: number
  /**
   * Готовое толкование — когда открываем уже сделанный сегодня расклад.
   * Вместе с ним едут id расклада на сервере и уже заданные по нему вопросы:
   * без id спрашивать некуда, а без переписки она пропала бы при переоткрытии.
   */
  saved?: {
    id?: string
    overall: string
    perCard: string[]
    readyAt?: number
    chat?: AskLine[]
    turnsLeft?: number
    /** Тема, с которой расклад сделан: по ней экран решает, менялась ли она. */
    theme?: string
  }
  /** Что человек сравнивает в «Двух путях» — уходит в промпт как вопрос. */
  question?: string
}

/**
 * Точка отсчёта для раскладов-прогнозов: Колесу года — текущий месяц,
 * Неделе — сегодняшний день (0 — понедельник). Оба смотрят вперёд, поэтому
 * начинаются с «сейчас», а не с января и не с понедельника.
 */
function startIndexFor(id: SpreadId): number {
  if (id === 'week') return (new Date().getDay() + 6) % 7
  return new Date().getMonth()
}

/** Сколько карт раскладывать веером в «Да или нет». */
const PICK_DECK_SIZE = 10

export function App() {
  const { profile, lang, t, remaining, update, anon } = useSirr()

  /**
   * Начальный экран.
   *
   * Обычно это главная, но после входа — тот экран, за которым человек и
   * пошёл входить. Вход через Google уводит со страницы целиком, приложение
   * возвращается заново, и без этой памяти покупатель оказывался на главной и
   * искал ту же кнопку второй раз. Намерение читается один раз и забывается,
   * иначе оно всплывало бы при каждом следующем запуске.
   */
  const [screen, setScreen] = useState<Screen>(() => {
    if (!profile.onboarded) return 'onboarding'
    // Пришли из уведомления — открываем то, ради чего звали. Проверяется ПОСЛЕ
    // знакомства: человека, который его не прошёл, нельзя высаживать посреди
    // приложения, и утреннего уведомления у него всё равно быть не может.
    const fromNotify = takeNotifyTab()
    if (fromNotify) return fromNotify
    const back = takeReturnScreen()
    return back === 'stars' ? 'stars' : 'home'
  })
  const [stack, setStack] = useState<Screen[]>([])
  const [obStep, setObStep] = useState(0)

  /*
    Награда за установку на экран.

    Два разных случая, и они не пересекаются:

    - запущено ИЗ УСТАНОВЛЕННОГО вида — забираем награду. Зовём при каждом
      запуске: защита от повторов живёт в самом запросе на сервере, и это
      надёжнее, чем помнить на клиенте (локальную память чистят, база помнит);
    - запущено в браузере — узнаём, сколько дают, чтобы предложение обещало
      ровно ту сумму, которую потом начислят. Число зашивать в текст нельзя:
      оно настраивается переменной окружения.

    Анониму не предлагаем и не забираем: ручка закрыта `requireAccount`, и
    обещание, которое не сбудется, хуже молчания.
  */
  const [installBonus, setInstallBonus] = useState(0)
  const [installGranted, setInstallGranted] = useState(0)
  useEffect(() => {
    if (authMode !== 'pwa' || anon) return
    if (isStandalone()) {
      api.pwaInstalled()
        .then((r) => {
          if (r.granted > 0) {
            setInstallGranted(r.granted)
            update({ stars: r.stars })
          }
        })
        .catch(() => {})
    } else {
      api.me().then((r) => setInstallBonus(r.ads?.install ?? 0)).catch(() => {})
    }
  }, [anon, update])
  const [reading, setReading] = useState<ActiveReading | null>(null)
  /** Разложенная колода для «Да или нет»; выбор человека превращает её в расклад. */
  const [deck, setDeck] = useState<DrawnCard[] | null>(null)
  /** Затравка расклада «Два пути», пока человек вводит варианты. */
  const [pendingPaths, setPendingPaths] = useState<string | null>(null)
  const [mainButton, setMainButton] = useState<MainButtonConfig | null>(null)
  /** Почему пользователь попал на экран Premium: кончился лимит или не хватает звёзд. */
  // `kind` нужен только ради текста: у гадания без карт «карты не лягут».
  const [paywall, setPaywall] = useState<
    { reason: 'limit' | 'stars'; cost?: number; kind?: 'spread' | 'fortune' } | null
  >(null)
  /** Открытое гадание без карт (кофе, ладонь, сон, персонаж). */
  const [fortune, setFortune] = useState<FormatId | null>(null)

  // Текущий экран в ref — чтобы startSpread знал, надо ли переходить на расклад,
  // не завися от screen (иначе колбэк пересоздаётся на каждый переход).
  const screenRef = useRef(screen)
  screenRef.current = screen

  // Тот же приём, что и со screenRef: `maybeInterstitial` зовётся из `goTab`,
  // а тому нельзя зависеть от расклада — иначе колбэк пересоздаётся на каждый
  // его шаг.
  const readingRef = useRef(reading)
  readingRef.current = reading

  /** Переход «вглубь» — экран кладётся в стек, чтобы работала кнопка «назад». */
  const push = useCallback((next: Screen) => {
    setScreen((current) => {
      setStack((s) => [...s, current])
      return next
    })
  }, [])

  /** Переключение вкладки — стек сбрасывается, «назад» становится не нужен. */
  /**
   * Полноэкранная реклама тем, кто не платит.
   *
   * Место выбрано осознанно: НЕ до расклада и НЕ сразу после текста — там
   * человек читает то, за чем пришёл, и перекрывать этот момент значит портить
   * главное. Показываем при уходе с готового расклада на любой другой экран,
   * и не чаще раза за сеанс: счётчик живёт в памяти вкладки, а не в хранилище,
   * — перезапуск приложения считается новым сеансом.
   *
   * Подписчику не показываем вовсе: отсутствие рекламы и есть то, за что он
   * заплатил. И НЕ показываем после платного расклада — по тому же правилу, по
   * которому его не задерживаем: человек только что отдал за него звёзды, и
   * встречать его за это рекламой нечестно.
   */
  const shownInterstitial = useRef(false)
  const maybeInterstitial = useCallback(() => {
    if (shownInterstitial.current) return
    if (profile.subscribed) return
    if (!adsAvailable()) return
    // Расклад, за который заплачено звёздами, рекламой не закрываем.
    const spread = readingRef.current?.spread
    if (spread && spreadById(spread).cost > 0) return
    shownInterstitial.current = true
    void showInterstitial()
  }, [profile.subscribed])

  const goTab = useCallback((tab: TabId) => {
    // Уходим с готового расклада — подходящий момент для показа: человек уже
    // получил своё и переключается дальше.
    if (screenRef.current === 'reading') maybeInterstitial()
    setStack([])
    setScreen(tab)
  }, [maybeInterstitial])

  const back = useCallback(() => {
    if (screen === 'onboarding' && obStep > 0) {
      setObStep((s) => (s === 2 && profile.langChosen ? 0 : s - 1))
      return
    }
    setStack((s) => {
      const next = [...s]
      const prev = next.pop() ?? 'home'
      setScreen(prev)
      return next
    })
  }, [screen, obStep, profile.langChosen])

  const startSpread = useCallback(
    (id: SpreadId, force = false) => {
      const def = spreadById(id)
      if (!def.available) {
        haptic.notify('warning')
        setPaywall(null)
        push('stars')
        return
      }

      const user = telegramUser()
      // «Карта дня» детерминирована (seed дня) и обязана совпадать с картой на
      // главном экране. Считаем её всегда сами; из истории берём только
      // толкование и только если карта в записи совпала — старые записи со
      // случайной картой (от прежних сборок) игнорируем.
      const dayCards =
        id === 'one' ? drawCards(`${user?.id ?? 'guest'}-${todayKey()}-day`, def.n) : null

      // Если сегодня такой расклад уже сделан и это не осознанное «заново» —
      // показываем тот же самый: те же карты, то же толкование, без списания.
      if (!force) {
        const startOfDay = new Date().setHours(0, 0, 0, 0)
        const todays = profile.history.find(
          (r) =>
            r.spread === id &&
            r.ts >= startOfDay &&
            // Тема — часть ключа: «Три карты про любовь» и «про карьеру» это
            // разные расклады, и подставлять один вместо другого нельзя.
            (r.theme ?? '') === (profile.theme ?? '') &&
            // Пустой текст у расклада, который ещё «готовится», — это не брак
            // записи, а ожидание: такой расклад тоже надо переоткрывать, а не
            // делать новый и тратить квоту второй раз.
            (r.overall || (r.readyAt ?? 0) > Date.now()) &&
            (dayCards === null || sameDraw(r.cards, dayCards)),
        )
        if (todays) {
          setReading({
            spread: id,
            cards: todays.cards,
            startMonth: startIndexFor(id),
            // id, вопросы и остаток — с сервера через историю: без id
            // спрашивать по раскладу некуда.
            saved: {
              id: todays.id,
              overall: todays.overall,
              perCard: todays.perCard,
              readyAt: todays.readyAt,
              chat: todays.chat,
              turnsLeft: todays.turnsLeft,
              theme: todays.theme ?? '',
            },
          })
          haptic.impact('light')
          if (screenRef.current !== 'reading') push('reading')
          return
        }
      }

      /*
        Цена: второй расклад того же вида за день дешевле — человек приходит
        с другой темой, но первый уже оплачен. Правило общее с сервером
        (`repeatCost` в `shared/spreads.ts`), и это обязательно: спишет всё
        равно сервер, и разойдись числа — на кнопке было бы одно, а в балансе
        другое.
      */
      const didToday = profile.history.some(
        (r) => r.spread === id && r.ts >= new Date().setHours(0, 0, 0, 0) && Boolean(r.overall),
      )
      const cost = didToday ? repeatCost(def.cost) : def.cost

      if (cost > 0) {
        // Платный расклад: звёзды. Сервер проверит баланс ещё раз и спишет
        // атомарно — эта проверка нужна лишь чтобы не тасовать карты зря.
        if (profile.stars < cost) {
          haptic.notify('error')
          setPaywall({ reason: 'stars', cost })
          push('stars')
          return
        }
      } else if (remaining <= 0) {
        haptic.notify('error')
        setPaywall({ reason: 'limit' })
        push('stars')
        return
      }
      const seed = `${user?.id ?? 'guest'}-${todayKey()}-${id}-${Date.now()}`

      // «Два пути» без вариантов бессмыслен: сначала спрашиваем, между чем
      // человек выбирает, и только потом тянем карты.
      if (id === 'paths') {
        setPendingPaths(seed)
        if (screenRef.current !== 'paths') push('paths')
        return
      }

      // «Да или нет»: карту выбирает человек. Раскладываем перед ним колоду и
      // ждём выбора — сам расклад соберётся из выбранной карты.
      if (id === 'yesno') {
        setDeck(drawCards(seed, PICK_DECK_SIZE))
        haptic.impact('light')
        if (screenRef.current !== 'pick') push('pick')
        return
      }

      // «Карта дня» — детерминированная карта дня; остальные расклады получают
      // уникальный seed на каждый запуск (новый расклад — свежие карты).
      const cards = dayCards ?? drawCards(seed, def.n)
      setReading({ spread: id, cards, startMonth: startIndexFor(id) })
      haptic.impact('medium')
      if (screenRef.current !== 'reading') push('reading')
    },
    [remaining, profile.stars, profile.history, push],
  )

  /**
   * Открыть гадание без карт.
   *
   * Баланс здесь не проверяем: списывает сервер, и единственная честная
   * проверка — его. Ритуал съёмки и ввод сна ничего не стоят, а отказ
   * человек увидит там, где нажмёт «прочитать», — с ценой перед глазами.
   */
  const openFormat = useCallback(
    (id: FormatId) => {
      const def = formatById(id)
      if (!def?.available) {
        haptic.notify('warning')
        setPaywall(null)
        push('stars')
        return
      }
      setFortune(id)
      haptic.impact('light')
      push('fortune')
    },
    [push],
  )

  const finishOnboarding = useCallback(() => {
    haptic.notify('success')
    update({ onboarded: true })
    track('onboarding_done')
    setStack([])
    setScreen('home')
  }, [update])

  const titles: Record<Screen, string> = {
    onboarding: 'SIRR',
    login: t('enter'),
    home: 'SIRR',
    catalog: t('catalogTitle'),
    fortune: fortune ? loc(formatById(fortune)!, lang) : t('catalogTitle'),
    reading: t('readingTitle'),
    pick: t('readingTitle'),
    paths: t('readingTitle'),
    profile: t('profileTitle'),
    compat: t('astroTitle'),
    stars: t('premiumTitle'),
    invites: t('invitesTitle'),
  }

  const isOnboardingWelcome = screen === 'onboarding' && obStep === 0
  const showTabs = screen === 'home' || screen === 'catalog' || screen === 'compat' || screen === 'profile'
  const canBack = stack.length > 0 || (screen === 'onboarding' && obStep > 0)
  // Считаем от числа вопросов, а не списком процентов: список молча врал бы
  // при добавлении вопроса — так и вышло, когда в вебе появилось имя.
  const progress = screen === 'onboarding' ? Math.round((obStep / OB_LAST) * 100) : 0

  const body = useMemo(() => {
    switch (screen) {
      case 'onboarding':
        return (
          <Onboarding
            step={obStep}
            // Вопрос про язык пропускаем, если человек уже ответил на него в
            // боте: переспрашивать значит показать, что его не услышали.
            /*
              Отмечаем ПРОЙДЕННЫЙ шаг, а не показанный.

              Из 47 открывших приложение за неделю расклад начали 15, и где
              теряются остальные — на знакомстве или уже на главной — было не
              видно: событий внутри знакомства не было ни одного. Уходит только
              номер шага; ответы личные и на этот вопрос не отвечают.

              Правила те же, что в нативе (`app/src/app/onboarding.tsx`), —
              иначе воронки двух платформ нельзя сложить.
            */
            onNext={() => {
              track('onboarding_step', { step: obStep })
              setObStep((s) => (s === 0 && profile.langChosen ? 2 : s + 1))
            }}
            onGoTo={setObStep}
            onFinish={finishOnboarding}
          />
        )
      case 'home':
        return (
          <Home
            onOpenCatalog={() => goTab('catalog')}
            onOpenStars={() => push('stars')}
            onStartSpread={startSpread}
            onOpenFormat={openFormat}
            onLogin={() => push('login')}
          />
        )
      case 'catalog':
        return <Catalog onStartSpread={startSpread} onOpenFormat={openFormat} />
      case 'fortune':
        return fortune ? (
          <Fortune
            key={fortune}
            format={fortune}
            onPaywall={(cost) => {
              setPaywall({ reason: 'stars', cost, kind: 'fortune' })
              push('stars')
            }}
          />
        ) : null
      case 'reading':
        return reading ? (
          <Reading
            key={reading.cards.map((c) => `${c.id}${c.reversed ? 'r' : ''}`).join('-') + (reading.saved ? '-s' : '-n')}
            spread={reading.spread}
            cards={reading.cards}
            startMonth={reading.startMonth}
            question={reading.question}
            saved={reading.saved ?? null}
            onNewReading={() => startSpread(reading.spread, true)}
            // Смена темы: без `force`. Если под выбранной темой расклад
            // сегодня уже был, человек получит его же и ничего не заплатит.
            onRetheme={() => startSpread(reading.spread)}
            onRepeated={(cards, saved) =>
              setReading((r) => (r ? { ...r, cards, saved } : r))
            }
            onPaywall={(reason) => {
              setPaywall({ reason, cost: spreadById(reading.spread).cost })
              push('stars')
            }}
          />
        ) : null
      case 'pick':
        return deck ? (
          <PickCard
            deck={deck}
            onPick={(card) => {
              setReading({ spread: 'yesno', cards: [card], startMonth: 0 })
              // Экран выбора заменяем раскладом, а не кладём поверх: «назад»
              // должно вести к списку раскладов, а не к уже сделанному выбору.
              setScreen('reading')
            }}
          />
        ) : null
      case 'paths':
        return pendingPaths ? (
          <TwoPaths
            onReady={(first, second) => {
              setReading({
                spread: 'paths',
                cards: drawCards(pendingPaths, spreadById('paths').n),
                startMonth: 0,
                question: `${first} / ${second}`,
              })
              setScreen('reading')
            }}
          />
        ) : null
      case 'profile':
        return <Profile onNewSpread={() => goTab('catalog')} onOpenInvites={() => push('invites')} onLogin={() => push('login')} />
      case 'invites':
        return <Invites onLogin={() => push('login')} />
      // Вход как обычный экран, а не как стена перед приложением. Тот же
      // компонент, что раньше стоял воротами: там все способы входа сразу —
      // и Google, и почта, — а карточка предложения даёт только один.
      case 'login':
        return <Login onDone={back} />
      case 'compat':
        return <Compat />
      case 'stars':
        return (
          <>
            {paywall && (
              <div
                style={{
                  margin: '18px 22px 0',
                  border: '1px solid rgba(224,122,84,.4)',
                  borderRadius: 14,
                  padding: '13px 15px',
                  background: 'rgba(74,30,20,.35)',
                }}
              >
                <div style={{ fontFamily: 'var(--serif)', fontStyle: 'italic', fontSize: 16 }}>
                  {paywall.reason === 'stars' && anon && paywall.kind === 'fortune'
                    ? t('firstFreeLogin')
                    : t(paywall.reason === 'stars' ? 'starsTitle' : 'limitTitle')}
                </div>
                <div style={{ fontSize: 12.5, color: 'var(--ink-muted)', marginTop: 3, lineHeight: 1.4 }}>
                  {paywall.reason !== 'stars'
                    ? t('limitBody', { n: FREE_LIMIT_PER_DAY })
                    : // Анониму «пополните баланс» предлагать нечем: покупка ему
                      // закрыта, и совет, которому нельзя последовать, читается
                      // как поломка. Говорим то, что он МОЖЕТ сделать.
                      anon && paywall.kind === 'fortune'
                      ? t('anonFreeWhy')
                      : t(paywall.kind === 'fortune' ? 'starsBodyFortune' : 'starsBody', {
                          n: paywall.cost ?? 0,
                          b: profile.stars,
                        })}
                </div>
              </div>
            )}
            <Stars onLogin={() => push('login')} />
          </>
        )
    }
  }, [screen, obStep, reading, deck, pendingPaths, paywall, fortune, profile.stars, finishOnboarding, goTab, push, startSpread, openFormat, t])

  return (
    <MainButtonContext.Provider value={setMainButton}>
      <div className="app">
        <Starfield />

        {!isOnboardingWelcome && (
          <Header
            title={titles[screen]}
            onBack={canBack ? back : undefined}
          />
        )}

        {screen === 'onboarding' && !isOnboardingWelcome && (
          <div className="progress">
            <div className="progress__fill" style={{ width: `${progress}%` }} />
          </div>
        )}

        {/* Полоса с обратным отсчётом льготной цены. Стоит ВНУТРИ каркаса,
            над телом, а не позиционируется поверх: `.app` не прокручивается
            (см. `pinFrame`), и полоса, положенная сверху абсолютом, накрыла бы
            содержимое. Показываем и в онбординге тоже — предложение живёт
            сутки, и человек, проходящий его десять минут, должен о нём знать.
            Подписчику полосы нет: сервер присылает ему нулевой срок. */}
        {!isOnboardingWelcome && <IntroStrip onOpen={() => push('stars')} />}

        {showTabs && <InstallBanner />}
        {/*
          Полоса «поставьте приложение» — на главной И на экране приветствия.

          Приветствие важнее: это первое, что видит человек с Android, и он ещё
          ничего не вложил в браузерную версию. Смысл затеи в том, чтобы он
          вместо сайта сразу оказался в приложении, а после знакомства
          предлагать это уже поздно — он прошёл шесть шагов и не станет
          проходить их заново.

          А вот В СЕРЕДИНЕ знакомства полосы нет намеренно: человек, который
          отвечает на вопросы, уже втянулся, и уводить его в магазин на
          полпути — верный способ потерять и то и другое.
        */}
        {(showTabs || isOnboardingWelcome) && <GetApp />}
        {showTabs && <InstallOffer bonus={installBonus} />}
        {/* Награда уже начислена — говорим об этом тем же окном, что и после
            ролика: человек только что поставил приложение и должен увидеть,
            что обещанное пришло. */}
        <RewardModal
          stars={installGranted}
          title={t('installDone')}
          body={t('installDoneBody', { n: installGranted })}
          ok={t('rewardOk')}
          onClose={() => setInstallGranted(0)}
        />
        {showTabs && <NotifyAsk />}

        <div className="app__body">{body}</div>

        {/* Окно при первом открытии — последним в каркасе: оно поверх всего,
            и порядок в разметке проще, чем гонка z-index. Показывается один
            раз за установку, дальше о предложении напоминает полоса. */}
        {showTabs && <IntroModal onOpen={() => push('stars')} />}

        {/* Главная кнопка выше навигации: она относится к тому, что на экране,
            а навигация уводит с него. Внизу под пальцем должно лежать
            действие, а не переключатель разделов. */}
        <div className="bottom">
          <MainButtonSlot config={mainButton} />
          {showTabs && <TabBar active={screen as TabId} lang={lang} onGo={goTab} />}
        </div>
      </div>
    </MainButtonContext.Provider>
  )
}
